const UNITS = ["B", "KB", "MB", "GB", "TB"];

/**
 * Props for this component
 */
interface FileChipProps {
  file: File;
  removeFile?: (file: File) => void;
  downloadable?: boolean;
  viewable?: boolean;
}

/**
 * This component proves a chip with the name and size of the file.
 *
 * @param file The relevant file
 * @param removeFile <i>Optional</i> A function to provide functionality to delete the file
 * @param downloadable <i>Optional</i> A flag to mark the file as downloadable
 * @param viewable <i>Optional</i> A flag to mark the file as viewable (Opens in new tab)
 */
export default function FileChip({file, removeFile, downloadable, viewable}: FileChipProps) {
  // Default optionals
  downloadable = !!downloadable;
  viewable = !!viewable;

  // Create an object URL for this chip (safe handling using state + cleanup)
  const url = URL.createObjectURL(file);

  /**
   * Format file size with the appropriate unit
   */
  const formatFileSize = (): string => {
    const bytes = file.size;
    if (bytes === 0) return "0 B";

    let computedSize = bytes;
    let unitIndex = 0;

    while (computedSize >= 1024 && unitIndex < UNITS.length - 1) {
      computedSize /= 1024;
      unitIndex++;
    }

    const rounded =
        computedSize % 1 === 0 ? computedSize.toString() : computedSize.toFixed(0);

    return `${rounded} ${UNITS[unitIndex]}`;
  };

  return <a href={downloadable || viewable ? url : undefined}
            target={viewable ? "_blank" : undefined}
            download={downloadable ? file.name : undefined}
            className="file-chip">
    <span className="file-chip-name">{file.name}</span>
    <span className="file-chip-size ms-2 text-muted">{formatFileSize()}</span>
    {removeFile && (
        <button type="button"
                className="file-chip-close btn btn-sm btn-link p-0 ms-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeFile(file);
                }}>
          <i className="bi bi-x-lg"/>
        </button>
    )}
  </a>;
}
